import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../redux/hooks';
import { fetchCommunityPosts } from '../../services/communityService';
import type { CommunityPost } from '../../types';
import './ProfilePage.css';

type LoaderStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

const ProfilePage: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const navigate = useNavigate();
  const [posts, setPosts] = useState<CommunityPost[]>([]);
  const [status, setStatus] = useState<LoaderStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const displayName = user?.name || user?.username || '社区用户';

  useEffect(() => {
    if (!user?.id) {
      setPosts([]);
      setStatus('idle');
      return;
    }
    setStatus('loading');
    setError(null);
    fetchCommunityPosts({ author_id: user.id, sort: 'latest', limit: 20 })
      .then((response) => {
        setPosts(response.items);
        setStatus('succeeded');
      })
      .catch((fetchError: unknown) => {
        const message = fetchError instanceof Error ? fetchError.message : '个人动态加载失败，请稍后再试。';
        setError(message);
        setStatus('failed');
      });
  }, [user?.id]);

  const totalLikes = useMemo(() => {
    return posts.reduce((sum, post) => sum + (post.stats?.likes ?? 0), 0);
  }, [posts]);

  const totalComments = useMemo(() => {
    return posts.reduce((sum, post) => sum + (post.stats?.comments ?? 0), 0);
  }, [posts]);

  const topTopics = useMemo(() => {
    const counter = new Map<string, { label: string; count: number }>();
    posts.forEach((post) => {
      const topics = post.topic_details?.length ? post.topic_details : post.topics.map((id) => ({ id, label: id }));
      topics.forEach((topic) => {
        const entry = counter.get(topic.id);
        if (entry) {
          entry.count += 1;
        } else {
          counter.set(topic.id, { label: topic.label, count: 1 });
        }
      });
    });
    return Array.from(counter.values())
      .sort((a, b) => b.count - a.count)
      .slice(0, 3);
  }, [posts]);

  const handleOpenPost = (postId: string) => {
  navigate(`/app/community/status/${postId}`);
  };

  const handleCompose = () => {
  navigate('/app/community/compose');
  };

  return (
    <div className="community-profile">
      <header>
        <div className="community-profile__avatar">
          {user?.avatar_url ? (
            <img src={user.avatar_url} alt={displayName} />
          ) : (
            <span>{displayName.slice(0, 1)}</span>
          )}
        </div>
        <div className="community-profile__intro">
          <h1>{displayName}</h1>
          <p>记录你的经典案例、调护心得与日常洞察。</p>
          <button type="button" onClick={handleCompose}>发布新贴</button>
        </div>
      </header>

      <section className="community-profile__stats">
        <div>
          <span>内容总数</span>
          <strong>{posts.length}</strong>
        </div>
        <div>
          <span>累计获赞</span>
          <strong>{totalLikes}</strong>
        </div>
        <div>
          <span>互动次数</span>
          <strong>{totalComments}</strong>
        </div>
      </section>

      {topTopics.length > 0 && (
        <section className="community-profile__topics">
          <h2>擅长话题</h2>
          <ul>
            {topTopics.map((topic) => (
              <li key={topic.label}>
                <span>#{topic.label}</span>
                <small>{topic.count} 篇内容</small>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="community-profile__feed">
        <div className="community-profile__feed-header">
          <h2>我的社区动态</h2>
          <button type="button" onClick={handleCompose}>新建帖子</button>
        </div>

        {status === 'loading' && <div className="community-profile__placeholder">内容加载中...</div>}
        {status === 'failed' && error && <div className="community-profile__error">{error}</div>}

        {status === 'succeeded' && posts.length === 0 && (
          <div className="community-profile__placeholder">
            还没有发布内容，立即分享你的养生观点吧。
          </div>
        )}

        {posts.length > 0 && (
          <ul className="community-profile__posts">
            {posts.map((post) => (
              <li key={post.post_id}>
                <article>
                  <header>
                    <h3>{post.title}</h3>
                    <time>{post.created_at ? new Date(post.created_at).toLocaleString() : '刚刚'}</time>
                  </header>
                  <p>{post.content.slice(0, 120)}{post.content.length > 120 ? '…' : ''}</p>
                  {post.topic_details?.length ? (
                    <div className="community-profile__post-topics">
                      {post.topic_details.map((topic) => (
                        <span key={topic.id}>#{topic.label}</span>
                      ))}
                    </div>
                  ) : null}
                  <footer>
                    <span>赞 {post.stats?.likes ?? 0}</span>
                    <span>评论 {post.stats?.comments ?? 0}</span>
                    <button type="button" onClick={() => handleOpenPost(post.post_id)}>查看详情</button>
                  </footer>
                </article>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default ProfilePage;
